import type { DashboardContextBootstrap } from '@seller-hermes/features/portal/context-bootstrap';
import type { SellerGroupUser } from '@seller-hermes/features/seller-group';
import { SellersQuery } from '@seller-hermes/features/sellers/queries/seller.query';

export const FIRST_SELLER_PATH = '/onboarding/first-seller';

// Telas que não dependem de filial: o próprio cadastro (senão vira loop),
// preferências e segurança da conta (2FA). Sair/voltar pro portal ficam na
// tela de cadastro.
const PATHS_WITHOUT_SELLERS = new Set([
  FIRST_SELLER_PATH,
  '/settings',
  '/settings/preferences',
  '/settings/security',
]);

/**
 * Espelha o guard do POST /seller no dashboard-api:
 * `@RequireAnyOfTheseUserTypes(MASTER, ADMIN)`, e INTERNAL passa por qualquer
 * exigência de tipo no SellerGroupGuard.
 */
export const canCreateSeller = (
  type: SellerGroupUser['type'] | null | undefined,
): boolean => type === 'MASTER' || type === 'ADMIN' || type === 'INTERNAL';

export type NoSellerDecision =
  /** Tem filial (no /me ou confirmada no lookup-ids): segue normal. */
  | 'has-sellers'
  /**
   * Sem filial confirmada: vai pra rota de cadastro, que decide entre
   * formulário e aviso olhando o grupo (ver `decideFirstSellerMode`).
   */
  | 'no-sellers'
  /** Fora do guard: INTERNAL, rota que não precisa de filial ou lookup falhou. */
  | 'skip';

type SellerIds = Array<{ id: string; active?: boolean }>;

/**
 * Decide o que fazer com quem chega ao painel sem filial no grupo ativo.
 *
 * O /me sozinho não basta: o wizard de primeira filial já foi desligado duas
 * vezes (#1097, #1107) porque o `sellers: []` vinha velho (store reidratado ou
 * cache) pra quem TINHA filial. Por isso o vazio do /me só vale depois de
 * confirmado no `GET /v2/sellers/lookup-ids`, chamado na hora, que filtra pelo
 * grupo ativo. Se a confirmação falhar, não prende ninguém: segue normal.
 *
 * `fetchSellerIds` só é chamado quando o /me veio sem filial, então quem tem
 * filial não paga request a mais.
 */
export const decideNoSellerRoute = async ({
  user,
  path,
  fetchSellerIds,
}: {
  user: Pick<SellerGroupUser, 'type' | 'sellers'>;
  path?: string;
  fetchSellerIds: () => Promise<SellerIds>;
}): Promise<NoSellerDecision> => {
  if ((user.sellers?.length ?? 0) > 0) return 'has-sellers';
  // Staff Abbiamo navega grupo de cliente sem vínculo: fica de fora.
  if (user.type === 'INTERNAL') return 'skip';
  if (path && PATHS_WITHOUT_SELLERS.has(path)) return 'skip';

  let ids: SellerIds;
  try {
    ids = await fetchSellerIds();
  } catch {
    return 'skip';
  }
  // Mesmo critério do /me: só filial ativa conta.
  if (ids.some((s) => s.active !== false)) return 'has-sellers';

  return 'no-sellers';
};

export type FirstSellerMode =
  /** Grupo sem filial ativa e o usuário pode criar: formulário. */
  | 'create'
  /** Grupo sem filial ativa e o usuário não pode criar: fale com o admin. */
  | 'ask-admin'
  /**
   * O grupo JÁ tem filial ativa, só o usuário que não tem vínculo (ex.: o
   * admin trocou o grupo/conta dele e apagou os users_sellers). Formulário
   * aqui cria filial duplicada; vale pra qualquer tipo. Também é o estado
   * quando não deu pra saber se o grupo tem filial.
   */
  | 'no-branch-access';

/**
 * O que a rota de cadastro mostra pra quem chegou sem filial.
 *
 * Zero filial nos vínculos do usuário não quer dizer grupo vazio: o /me e o
 * lookup-ids olham só os users_sellers dele. Antes de oferecer o formulário,
 * pergunta ao grupo. Na dúvida (erro, resposta estranha), nunca formulário.
 */
export const decideFirstSellerMode = async ({
  type,
  groupHasActiveSellers,
}: {
  type: SellerGroupUser['type'] | null | undefined;
  groupHasActiveSellers: () => Promise<boolean>;
}): Promise<FirstSellerMode> => {
  let hasSellers: boolean;
  try {
    hasSellers = await groupHasActiveSellers();
  } catch {
    return 'no-branch-access';
  }
  if (hasSellers !== false) return 'no-branch-access';
  return canCreateSeller(type) ? 'create' : 'ask-admin';
};

/**
 * Pergunta ao dashboard-api se o grupo ativo tem filial ativa.
 *
 * 1. `GET /v2/sellers/group-status` (`{ has_active_sellers }`), aberto a
 *    qualquer tipo. Resposta sem booleano conta como erro.
 * 2. Se ele não existir ainda (backend antigo) e o usuário for MASTER ou
 *    INTERNAL: `GET /v2/sellers/grantable`, que pra esses tipos lista todas as
 *    filiais do grupo, com ou sem vínculo. Pros demais tipos o grantable só
 *    devolve as próprias filiais (vazio aqui), então não serve: propaga o erro
 *    e a decisão cai no estado sem formulário.
 */
export const resolveGroupHasActiveSellers = async ({
  type,
  fetchGroupStatus,
  fetchGrantable,
}: {
  type: SellerGroupUser['type'] | null | undefined;
  fetchGroupStatus: () => Promise<{ has_active_sellers?: unknown }>;
  fetchGrantable: () => Promise<SellerIds>;
}): Promise<boolean> => {
  try {
    const { has_active_sellers } = await fetchGroupStatus();
    if (typeof has_active_sellers === 'boolean') return has_active_sellers;
    throw new Error('group-status sem has_active_sellers');
  } catch (err) {
    if (type !== 'MASTER' && type !== 'INTERNAL') throw err;
    const sellers = await fetchGrantable();
    if (!Array.isArray(sellers)) throw err;
    // Campo ausente conta como ativa: na dúvida, sem formulário.
    return sellers.some((s) => s.active !== false);
  }
};

export const fetchGroupHasActiveSellersFresh = (
  type: SellerGroupUser['type'] | null | undefined,
): Promise<boolean> =>
  resolveGroupHasActiveSellers({
    type,
    fetchGroupStatus: SellersQuery.groupStatus,
    fetchGrantable: SellersQuery.grantable,
  });

// Loader do layout e da rota filha rodam juntos na mesma navegação: os dois
// caem aqui, e só um request sai.
let inFlight: Promise<SellerIds> | null = null;
export const fetchSellerIdsFresh = (): Promise<SellerIds> => {
  if (!inFlight) {
    inFlight = SellersQuery.lookupIds().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};

/**
 * Dados da tela de cadastro quando o /me deu 403 (sem filial ativa no grupo):
 * o tipo do usuário NESTE grupo vem do /portal/me (`seller_groups[].type`), a
 * mesma autoridade que o guard do POST /seller usa. Sem tipo, ninguém ganha o
 * formulário: cai no aviso de sem acesso.
 */
export interface NoSellerContext {
  type: SellerGroupUser['type'] | null;
  sellerGroupName: string | null;
  email: string | null;
}

export const noSellerContextFromPortal = (
  ctx: Pick<DashboardContextBootstrap, 'portalMe' | 'sellerGroup'> | null,
): NoSellerContext => ({
  type: ctx?.sellerGroup?.type ?? null,
  sellerGroupName: ctx?.sellerGroup?.name ?? null,
  email: ctx?.portalMe?.user?.email ?? null,
});

/** O que o loader da rota de cadastro entrega pra página. */
export interface FirstSellerLoaderData extends NoSellerContext {
  mode: FirstSellerMode;
}

/**
 * Regra do loader da rota de cadastro, fora do roteador pra dar pra testar.
 * Roda depois do guard de sessão. Vale também pra quem abre a URL direto:
 * grupo com filial ativa nunca recebe o modo 'create'.
 */
export const resolveFirstSellerPage = async ({
  noActiveSellers,
  me,
  getPortalContext,
  fetchSellerIds,
  groupHasActiveSellers,
}: {
  noActiveSellers: boolean;
  me: Pick<SellerGroupUser, 'type' | 'sellers' | 'email' | 'seller_group'> | null;
  getPortalContext: () => Promise<NoSellerContext>;
  fetchSellerIds: () => Promise<SellerIds>;
  groupHasActiveSellers: (
    type: SellerGroupUser['type'] | null | undefined,
  ) => Promise<boolean>;
}): Promise<{ redirectTo: string } | FirstSellerLoaderData> => {
  let context: NoSellerContext;
  if (noActiveSellers) {
    // /me deu 403: tipo no grupo, nome e e-mail vêm do /portal/me.
    context = await getPortalContext();
  } else {
    if (!me) return { redirectTo: '/login' };
    // Só fica aqui quem está mesmo sem filial (mesma regra do guard do
    // painel), senão não sai do onboarding quem já tem filial.
    const decision = await decideNoSellerRoute({ user: me, fetchSellerIds });
    if (decision === 'has-sellers') return { redirectTo: '/' };
    context = {
      type: me.type,
      sellerGroupName: me.seller_group?.name ?? null,
      email: me.email ?? null,
    };
  }
  const mode = await decideFirstSellerMode({
    type: context.type,
    groupHasActiveSellers: () => groupHasActiveSellers(context.type),
  });
  return { ...context, mode };
};
